import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IntersectionObserverService } from '@app/core/services/intersection-observer.service';
import { CoverageModal } from '@app/my-zone/shared/modals';
import { CompareTableControlService } from '@app/shared/services/compare-table-control.service';
import { CoverageIdEnum, NewModal } from 'outshared-lib';
import { Observable } from 'rxjs';
import {
    WucCompareTableCoverageDataActionInterface,
    WucCompareTableCoverageDataColumnInterface,
    WucCompareTableCoverageDataInterface, WucCompareTableCoverageModule,
} from '@inshared/website-ui-components';
import { CoverageDescriptionInterface, CoverageItemOptionInterface } from '@app/core/interfaces';
// eslint-disable-next-line max-len
import { NoCoverageModalComponent } from '@app/shared/modals/coverage-modals/no-coverage-modal/no-coverage-modal.component';
import { CoverageGroupTable } from '../../classes';
import { CoverageItemInterface2, CoverageTableConfigInterface } from '../../interfaces';
import { CommonModule } from '@angular/common';
import { SwipeAnimationModule } from '@app/shared/ui/swipe-animation/swipe-animation.module';
import { PipesModule } from '@app/shared/pipes/pipes.module';
import { ScreenSizeModule } from '@app/shared/directives/screen-size.module';
import { NoCoverageModalModule } from '@app/shared/modals/coverage-modals/no-coverage-modal/no-coverage-modal.module';

@Component({
    selector: 'ins-coverage-table',
    templateUrl: './coverage-table.component.html',
    styleUrls: ['./coverage-table.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        NoCoverageModalModule,
        WucCompareTableCoverageModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CoverageTableComponent),
            multi: true,
        },
    ],
})
export class CoverageTableComponent implements ControlValueAccessor, OnInit {
    @Input() public group: CoverageGroupTable;
    public selectedCoverageId: CoverageIdEnum;
    public isScreenLarge$: Observable<boolean>;
    public isScreenMedium$: Observable<boolean>;
    public propagateChange;
    public subscriptions;
    public data!: WucCompareTableCoverageDataInterface;
    public value: { choice: string[] } = {
        choice: [],
    };

    constructor(
        private intersectionObserverService: IntersectionObserverService,
        private compareTableControlService: CompareTableControlService,
        private newModal: NewModal
    ) {
        this.isScreenLarge$ = this.intersectionObserverService.isScreenLarge$;
        this.isScreenMedium$ = this.intersectionObserverService.isScreenMedium$;
    }

    public ngOnInit(): void {
        this.setData(this.group.coverages);
    }

    private getMostPopularTitle(): string {
        return this.group.coverages.items.find((item): boolean => item.id === this.group.mostPopular)?.title ?? '';
    }

    private roundUpAndRemoveDecimals(price: number): string {
        return (Math.round(price * 100) / 100).toFixed(2);
    }

    private getActions(currentItem: CoverageItemInterface2): WucCompareTableCoverageDataActionInterface[] {
        return [
            {
                type: 'button',
                isDisabled: currentItem.disabled,
                label: this.getButtonText(currentItem),
                labelMobile: this.getButtonText(currentItem),
                isPrimary: this.selectedCoverageId === currentItem.id,
                callback: (): void => this.setSelected(currentItem.id as CoverageIdEnum, currentItem.disabled),
            },
            {
                type: 'link',
                label: 'Meer info',
                labelMobile: 'Meer info',
                callback: (): boolean => this.openModal(currentItem.id as CoverageIdEnum),
            },
        ];
    }

    private setData(coverages: CoverageTableConfigInterface): void {
        this.data = {
            extraInfo: {
                columnKey: this.group.mostPopular,
                title: 'Meest gekozen',
                // eslint-disable-next-line max-len
                subTitle: `Andere verzekerden bij InShared met een vergelijkbare situatie, kozen voor ${this.getMostPopularTitle()}`,
            },
            labels: [
                'Premie per maand',
                ...coverages.descriptions.map((description: CoverageDescriptionInterface): string => description.label),
            ],
            columns: coverages.items.map(
                (item: CoverageItemInterface2): WucCompareTableCoverageDataColumnInterface => ({
                    key: item.id,
                    title: item.title,
                    subTitle: item.subtitle,
                    disabled: item.disabled,
                    rows: [
                        {
                            items: [
                                {
                                    type: item.disabled ? 'text-large' : 'price',
                                    value: item.disabled ? 'NVT' : this.roundUpAndRemoveDecimals(item.price),
                                },
                            ],
                        },
                        ...item.options.map((option: CoverageItemOptionInterface) => ({
                            items: [
                                {
                                    type: (option.type === 'string' ? 'text' : 'boolean') as 'text' | 'boolean',
                                    value: option.value,
                                },
                            ],
                        })),
                    ],
                    actions: this.getActions(item),
                })
            ),
        };
    }

    private getButtonText(item: CoverageItemInterface2): string {
        if (item.disabled) {
            return 'NVT';
        }

        return item.id === this.selectedCoverageId ? 'Gekozen' : 'Kiezen';
    }

    public updateActions(): void {
        this.data.columns.forEach((column): void => {
            const currentCoverage = this.group.coverages.items.find((item): boolean => item.id === column.key);
            column.actions = this.getActions(currentCoverage);
        });
    }

    public onClicked(coverageId: CoverageIdEnum): void {
        const currentCoverage = this.group.coverages.items.find((item): boolean => item.id === coverageId);
        this.setSelected(coverageId, currentCoverage.disabled);
    }

    public setSelected(coverageId: CoverageIdEnum, disabled: boolean): void {
        if (!coverageId) {
            return;
        }

        if (disabled) {
            this.newModal.open<NoCoverageModalComponent>(NoCoverageModalComponent);
            return;
        }

        this.value.choice = this.group.findSelectedCoverageIds(coverageId);
        this.setSelectedCoverage(coverageId);
        this.propagateChange();
    }

    public openModal(coverageId: CoverageIdEnum): boolean {
        const coverageInput = this.group.getCoverageInput(coverageId);
        const modalRef = this.newModal.open(CoverageModal);
        modalRef.componentInstance.coverageInput = coverageInput;

        return false;
    }

    public writeValue(coverageIds: CoverageIdEnum[]): void {
        if (coverageIds && coverageIds.length > 0) {
            const coverageId = this.group.findSelectedCoverageId(coverageIds);
            this.setSelectedCoverage(coverageId);
        }
    }

    public registerOnChange(fn: any): void {
        this.propagateChange = (): any => {
            fn(this.value.choice);
        };
    }

    public registerOnTouched(): void {
        return;
    }

    // Feels a little buggy to use compareTableControlService
    private setSelectedCoverage(coverageId: CoverageIdEnum): void {
        const coverage = this.group.getCoverageItem(coverageId);
        this.selectedCoverageId = coverageId;
        this.compareTableControlService.setSelectedCoverage(coverage);
        this.updateActions();
    }
}
